import { DataTable, DataTableSortStatus } from 'mantine-datatable';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { ActionIcon, Button, Group, TextInput, Tooltip } from '@mantine/core';
import { openConfirmModal, openModal } from '@mantine/modals';
import { showNotification } from '@mantine/notifications';
import {
  IconGenderBigender,
  IconPencil,
  IconPlus,
  IconSearch,
  IconTrash,
  IconX,
} from '@tabler/icons-react';

import {
  Gender,
  GendersSortBy,
  GetGendersRequestParams,
} from '@interfaces/genders.interface';

import {
  useCountGendersQuery,
  useDeleteGenderMutation,
  useGetManyGendersQuery,
} from '@api/genders.api';
import { isApiError } from '@api/index';

import useLayoutProps from '@hooks/useLayoutProps';
import useStateWithDebounce from '@hooks/useStateWithDebouce';
import useUserPermissionGuard from '@hooks/useUserPermissionGuard';

import UpsertGenderForm, {
  UPSERT_GENDER_FORM_MODAL_ID,
} from '@components/genders/UpsertGenderForm';

export default function GendersList() {
  // ==========================================================================
  // General
  // ==========================================================================
  const canCreateGenders = useUserPermissionGuard('GENDERS.CREATE');

  const canEditGenders = useUserPermissionGuard('GENDERS.UPDATE');
  const canDeleteGenders = useUserPermissionGuard('GENDERS.DELETE');

  // ==========================================================================
  // State
  // ==========================================================================
  const [filters, setFilters] = useState<GetGendersRequestParams>({
    page: +1,
    pageLength: +10,
    sortBy: 'name',
    sortOrder: 'asc',
  });

  const [nameSearch, setNameSearch, nameSearchDebounced] =
    useStateWithDebounce('');

  const [sortStatus, setSortStatus] = useState<DataTableSortStatus<Gender>>({
    columnAccessor: 'name',
    direction: 'asc',
  });

  useEffect(() => {
    // Skip the initial mount
    if (
      filters.sortBy === sortStatus.columnAccessor &&
      filters.sortOrder === sortStatus.direction
    ) {
      return;
    }

    setFilters((filters) => ({
      ...filters,
      sortBy: sortStatus.columnAccessor as GendersSortBy,
      sortOrder: sortStatus.direction,
    }));
  }, [sortStatus]);

  // ==========================================================================
  // Api
  // ==========================================================================
  const { data: gendersCount = { count: 0 }, isLoading: isLoadingCount } =
    useCountGendersQuery({
      nameSearch: nameSearchDebounced,
    });

  const { data = [], isLoading } = useGetManyGendersQuery({
    ...filters,
    nameSearch: nameSearchDebounced,
  });

  const [deleteGender, { isLoading: isLoadingDeleteGender }] =
    useDeleteGenderMutation();

  // ==========================================================================
  // Handlers
  // ==========================================================================
  const handleAddGenderClick = useCallback(() => {
    openModal({
      modalId: UPSERT_GENDER_FORM_MODAL_ID,
      title: 'Aggiungi genere',

      children: <UpsertGenderForm />,
    });
  }, []);

  const handleEditGenderClick = useCallback((gender: Gender) => {
    openModal({
      modalId: UPSERT_GENDER_FORM_MODAL_ID,
      title: 'Modifica genere',
      children: <UpsertGenderForm gender={gender} />,
    });
  }, []);

  const handleDeleteGenderClick = useCallback((id: string) => {
    openConfirmModal({
      title: 'Elimina genere',
      children:
        "Stai per eliminare il genere. L'operazione non è reversibile.\nContinuare?",

      confirmProps: { color: 'red' },
      labels: { confirm: 'Conferma', cancel: 'Annulla' },
      onConfirm: async () => {
        try {
          await deleteGender(id).unwrap();

          showNotification({
            title: 'Genere eliminato',
            message: 'Genere eliminato con successo',
          });
        } catch (e) {
          if (isApiError(e)) {
            if (e.data.code === 'P2003') {
              showNotification({
                title: 'Impossibile eliminare',
                message: 'Il genere è in uso da un paziente',
                color: 'red',
                icon: <IconX />,
              });
            } else {
              showNotification({
                title: 'Errore',
                message: e.data.message,
                color: 'red',
                icon: <IconX />,
              });
            }
          } else {
            showNotification({
              title: 'Errore',
              message: 'Errore sconosciuto',
              color: 'red',
              icon: <IconX />,
            });
            console.error(e);
          }
        }
      },
    });
  }, []);

  // ==========================================================================
  // Render
  // ==========================================================================
  const title = useMemo(
    () => (
      <Group>
        <IconGenderBigender />
        Generi {!isLoadingCount ? `(${gendersCount.count})` : ''}
      </Group>
    ),
    [gendersCount.count, isLoadingCount],
  );

  const rightSection = useMemo(
    () =>
      canCreateGenders ? (
        <Button leftSection={<IconPlus />} onClick={handleAddGenderClick}>
          Aggiungi genere
        </Button>
      ) : (
        <></>
      ),
    [handleAddGenderClick, canCreateGenders],
  );

  useLayoutProps({
    title,
    rightSection,
  });

  return (
    <>
      <DataTable
        /* General */
        striped
        noRecordsText="Nessun genere trovato"
        minHeight={data.length === 0 ? 200 : undefined}
        /* Data */
        fetching={isLoading || isLoadingCount}
        columns={[
          {
            accessor: 'name',
            title: 'Nome',
            sortable: true,
            filter: (
              <TextInput
                placeholder="Ricerca nome"
                leftSection={<IconSearch size={16} />}
                rightSection={
                  nameSearch.trim() !== '' ? (
                    <ActionIcon
                      size="sm"
                      variant="transparent"
                      c="dimmed"
                      onClick={() => setNameSearch('')}
                    >
                      <IconX size={14} />
                    </ActionIcon>
                  ) : undefined
                }
                value={nameSearch}
                onChange={(e) => setNameSearch(e.currentTarget.value)}
              />
            ),
          },
          {
            accessor: 'actions',
            title: '',
            textAlign: 'right',
            width: '0%',
            render: (record) => (
              <Group wrap="nowrap" gap="xs">
                {canEditGenders && (
                  <Tooltip label="Modifica genere">
                    <ActionIcon
                      variant="subtle"
                      onClick={() => handleEditGenderClick(record)}
                    >
                      <IconPencil />
                    </ActionIcon>
                  </Tooltip>
                )}
                {canDeleteGenders && (
                  <Tooltip label="Elimina genere">
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      disabled={isLoadingDeleteGender}
                      onClick={() => handleDeleteGenderClick(record.id)}
                    >
                      <IconTrash />
                    </ActionIcon>
                  </Tooltip>
                )}
              </Group>
            ),
          },
        ]}
        records={data}
        /* Sorting */
        sortStatus={sortStatus}
        onSortStatusChange={setSortStatus}
        /* Pagination */
        page={filters.page!}
        recordsPerPage={filters.pageLength!}
        totalRecords={gendersCount.count}
        paginationText={() => ''}
        recordsPerPageLabel=""
        recordsPerPageOptions={[10, 20, 50]}
        onPageChange={(newPage) => setFilters({ ...filters, page: newPage })}
        onRecordsPerPageChange={(newPageLength) =>
          setFilters({ ...filters, pageLength: newPageLength, page: 1 })
        }
      />
    </>
  );
}
